/**
 * Import/Export API service with TanStack React Query hooks
 */

import { useMutation } from '@tanstack/react-query'
import { apiPost, getAuthToken } from './client'

export interface ImportResponse {
  message: string
  success: number
  failed: number
  errors: Array<{
    row: number
    type: 'asset' | 'sale'
    data: Record<string, string>
    errors: string[]
  }>
}

/**
 * Import CSV data
 */
export function useImportData() {
  return useMutation({
    mutationFn: async ({ file, type }: { file: File; type: 'assets' | 'sales' }) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('type', type)

      const response = await apiPost<ImportResponse>('/api/import-export/import', formData, {
        headers: {}, // Let browser set Content-Type for FormData
      })
      return response
    },
    onSuccess: () => {
      // Invalidate queries to refresh data
      // Note: This would need to be done via queryClient, but we'll handle it in component
    },
  })
}

/**
 * Export data
 */
export async function exportData(type: 'assets' | 'sales' | 'all'): Promise<void> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787'
  const token = await getAuthToken()

  const headers: HeadersInit = {
    Accept: type === 'all' ? 'application/json' : 'text/csv',
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE_URL}/api/import-export/export?type=${type}`, {
    method: 'GET',
    headers,
  })

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`
    try {
      const errorData = await response.json()
      errorMessage = errorData.message || errorData.error || errorMessage
    } catch {
      // If response is not JSON, use status text
    }
    throw new Error(errorMessage)
  }

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${type}_${Date.now()}.${type === 'all' ? 'json' : 'csv'}`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

